import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, User, Mail, Building, MessageSquare, AlertCircle } from 'lucide-react';
import api from '../../api/apiClient';
import PageHeader from '../../components/PageHeader';

interface LeaseDetail {
  id: number;
  propertyId: number;
  propertyAddress: string;
  status: string;
  startDate: string;
  endDate?: string;
  rentAmount: number;
  rentFrequency: string;
  securityDeposit?: number;
  daysUntilExpiry?: number;
  tenantName?: string;
  tenantEmail?: string;
  agentName?: string;
  agentEmail?: string;
  property?: {
    id: number;
    address: string;
    type: string;
    bedrooms?: number;
    bathrooms?: number;
  };
  tenant?: {
    id: number;
    name: string;
    email: string;
    phone?: string;
  };
}

export default function OwnerLeaseDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lease, setLease] = useState<LeaseDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadLease();
    }
  }, [id]);

  const loadLease = async () => {
    setLoading(true);
    try {
      const response = await api.get<LeaseDetail>(`/owner/leases/${id}`);
      setLease(response);
    } catch (err) {
      console.error('Failed to load lease:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE': return 'bg-green-100 text-green-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'EXPIRED': return 'bg-red-100 text-red-800';
      case 'TERMINATED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatRent = (amount: number, frequency: string) => {
    const freq = frequency?.toLowerCase().replace('ly', '') || 'week';
    return `$${amount?.toLocaleString() || 0}/${freq}`;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!lease) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-600">Lease not found</p>
        <button onClick={() => navigate('/owner/leases')} className="mt-4 text-secondary hover:text-primary">
          Back to Leases
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <PageHeader
        title="Lease Details"
        subtitle={lease.propertyAddress || lease.property?.address}
        icon={<FileText className="w-8 h-8" />}
        backPath="/owner/leases"
      >
        <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(lease.status)}`}>
          {lease.status}
        </span>
        {lease.agentName && lease.status === 'ACTIVE' && (
          <button
            onClick={() => navigate(`/messages?type=LEASE&id=${lease.id}`)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90"
          >
            <MessageSquare className="w-5 h-5" />
            Message Agent
          </button>
        )}
      </PageHeader>

      {/* Expiry Warning */}
      {lease.daysUntilExpiry !== undefined && lease.daysUntilExpiry <= 30 && lease.daysUntilExpiry > 0 && (
        <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-orange-600" />
          <span className="text-orange-800">
            This lease expires in {lease.daysUntilExpiry} days ({formatDate(lease.endDate)}). Contact your agent to discuss renewal.
          </span>
        </div>
      )}

      {/* Info Banner */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-800 text-sm">
          <strong>Note:</strong> To request changes to this lease (rent adjustment, early termination, etc.), please message your property agent using the button above.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Lease Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-gray-900">Lease Information</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Start Date</p>
                <p className="font-medium text-gray-900">{formatDate(lease.startDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">End Date</p>
                <p className="font-medium text-gray-900">{formatDate(lease.endDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Rent Amount</p>
                <p className="font-medium text-gray-900">{formatRent(lease.rentAmount, lease.rentFrequency)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Frequency</p>
                <p className="font-medium text-gray-900">{lease.rentFrequency}</p>
              </div>
              {lease.securityDeposit && (
                <div>
                  <p className="text-sm text-gray-500">Security Deposit</p>
                  <p className="font-medium text-gray-900">${lease.securityDeposit.toLocaleString()}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(lease.status)}`}>
                  {lease.status}
                </span>
              </div>
            </div>
          </div>

          {/* Property Details */}
          {lease.property && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <Building className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Property</h2>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-gray-900">{lease.property.address}</p>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>{lease.property.type || 'Property'}</span>
                  {lease.property.bedrooms && <span>{lease.property.bedrooms} beds</span>}
                  {lease.property.bathrooms && <span>{lease.property.bathrooms} baths</span>}
                </div>
                <button
                  onClick={() => navigate(`/owner/properties/${lease.property?.id}`)}
                  className="text-sm text-secondary hover:text-primary"
                >
                  View Property →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Tenant Info */}
          {(lease.tenant || lease.tenantName) && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Tenant</h2>
              </div>
              <div className="space-y-3">
                <p className="font-medium text-gray-900">{lease.tenant?.name || lease.tenantName}</p>
                <p className="text-sm text-gray-500">Contact tenant through your managing agent</p>
              </div>
            </div>
          )}

          {/* Agent Info */}
          {lease.agentName && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <Building className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Managing Agent</h2>
              </div>
              <div className="space-y-3">
                <p className="font-medium text-gray-900">{lease.agentName}</p>
                {lease.agentEmail && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4" />
                    {lease.agentEmail}
                  </div>
                )}
                {lease.status === 'ACTIVE' && (
                  <button
                    onClick={() => navigate(`/messages?type=LEASE&id=${lease.id}`)}
                    className="w-full mt-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 text-sm"
                  >
                    Send Message
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Actions Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Need Changes?</h2>
            <p className="text-sm text-gray-600 mb-4">
              To request any of the following, please message your agent:
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Rent adjustment</li>
              <li>• Lease renewal</li>
              <li>• Early termination</li>
              <li>• Lease modification</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}


